import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import './Benchmark.css';

const API = '/api/v2';

function Benchmark() {
  const [campaign, setCampaign] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(API + '/campaigns').then(r => {
      const camps = r.data.campaigns || [];
      setCampaigns(camps);
      const main = camps.find(c => c.patient_count > 0);
      if (main) setCampaign(main.slug);
    });
  }, []);

  useEffect(() => {
    if (!campaign) return;
    setLoading(true);
    axios.get(API + '/benchmark/' + campaign).then(r => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [campaign]);

  if (loading) return <div className="bm-loading"><div className="spinner"></div>Loading benchmarks...</div>;
  if (!data) return <div className="bm-loading">Select a campaign to view your benchmarks.</div>;
  if (data.error === 'no_data') return (
    <div className="bm-empty">
      <div className="bm-empty-icon">📊</div>
      <h2>No Data Yet</h2>
      <p>Upload patient data to see your outcomes compared to the network.</p>
    </div>
  );

  const m = data.mine;
  const n = data.network;

  // Build comparison metrics
  const comparisons = [
    { label: 'Avg Age', mine: m.demographics.age.mean, network: n.demographics.age.mean, unit: ' yr', neutral: true },
    { label: 'Avg BMI', mine: m.demographics.bmi.mean, network: n.demographics.bmi.mean, unit: '', neutral: true },
    { label: 'Pre-op ODI', mine: m.preop.odi.mean, network: n.preop.odi.mean, unit: '', neutral: true },
    { label: 'Pre-op VAS Back', mine: m.preop.vas_back.mean, network: n.preop.vas_back.mean, unit: '', neutral: true },
    { label: 'Op Time', mine: m.surgery.op_time.mean, network: n.surgery.op_time.mean, unit: ' min', lower: true },
    { label: 'Length of Stay', mine: m.surgery.los.mean, network: n.surgery.los.mean, unit: ' d', lower: true },
    { label: 'Implants', mine: m.surgery.implants.mean, network: n.surgery.implants.mean, unit: '', neutral: true },
    { label: 'Complication Rate', mine: m.complications.rate, network: n.complications.rate, unit: '%', lower: true },
    { label: 'Improvement 3mo', mine: m.outcomes.improvement_3mo.mean, network: n.outcomes.improvement_3mo.mean, unit: '%', higher: true },
    { label: 'Improvement 12mo', mine: m.outcomes.improvement_12mo.mean, network: n.outcomes.improvement_12mo.mean, unit: '%', higher: true },
    { label: 'Satisfaction 3mo', mine: m.outcomes.satisfaction_3mo.rate, network: n.outcomes.satisfaction_3mo.rate, unit: '%', higher: true },
    { label: 'Satisfaction 12mo', mine: m.outcomes.satisfaction_12mo.rate, network: n.outcomes.satisfaction_12mo.rate, unit: '%', higher: true },
  ];

  const getDelta = (row) => {
    if (row.mine === null || row.network === null) return null;
    return Math.round((row.mine - row.network) * 10) / 10;
  };

  const getDeltaColor = (row) => {
    const d = getDelta(row);
    if (d === null || d === 0 || row.neutral) return '#64748b';
    if (row.lower) return d < 0 ? '#059669' : d > 0 ? '#dc2626' : '#64748b';
    if (row.higher) return d > 0 ? '#059669' : d < 0 ? '#dc2626' : '#64748b';
    return '#64748b';
  };

  const getDeltaLabel = (row) => {
    const d = getDelta(row);
    if (d === null) return '—';
    const sign = d > 0 ? '+' : '';
    return sign + d + row.unit;
  };

  // Radar data
  const radarData = [
    { metric: 'Improvement', mine: m.outcomes.improvement_3mo.mean || 0, network: n.outcomes.improvement_3mo.mean || 0 },
    { metric: 'Satisfaction', mine: m.outcomes.satisfaction_3mo.rate || 0, network: n.outcomes.satisfaction_3mo.rate || 0 },
    { metric: 'Low Complications', mine: Math.max(0, 100 - (m.complications.rate || 0)), network: Math.max(0, 100 - (n.complications.rate || 0)) },
    { metric: 'Short Op Time', mine: m.surgery.op_time.mean ? Math.max(0, 100 - m.surgery.op_time.mean) : 0, network: n.surgery.op_time.mean ? Math.max(0, 100 - n.surgery.op_time.mean) : 0 },
    { metric: 'Short Stay', mine: m.surgery.los.mean ? Math.max(0, 100 - m.surgery.los.mean * 10) : 0, network: n.surgery.los.mean ? Math.max(0, 100 - n.surgery.los.mean * 10) : 0 },
  ];

  // Distribution overlay data
  const distCharts = [
    { label: 'Age Distribution', key: 'age', color: '#2563eb' },
    { label: 'BMI Distribution', key: 'bmi', color: '#7c3aed' },
    { label: 'Pre-op ODI Distribution', key: 'preop_odi', color: '#059669' },
    { label: 'Op Time Distribution', key: 'op_time', color: '#d97706' },
  ];

  return (
    <div className="bm-page">
      <div className="bm-header">
        <div>
          <h1>{data ? data.primary_label : 'My Practice'}</h1>
          <p className="bm-sub">{data?.center_name ? data.center_name + ' — ' : ''}Outcomes compared to the SpineBase network</p>
        </div>
        <select className="bm-campaign" value={campaign} onChange={e => setCampaign(e.target.value)}>
          {campaigns.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
      </div>

      <div className="bm-disclaimer">
        ⚠️ For internal quality improvement only. Not for public reporting. Individual outcomes may vary based on patient selection and case complexity.
      </div>

      {/* Summary Cards */}
      <div className="bm-summary">
        <div className="bms-card accent">
          <span className="bms-val">{m.n}</span>
          <span className="bms-label">{data.has_personal ? 'My Cases' : data.center_name || 'My Center'}</span>
        </div>
        <div className="bms-card">
          <span className="bms-val">{n.n}</span>
          <span className="bms-label">Network Total</span>
        </div>
        <div className="bms-card">
          <span className="bms-val">{data.center_count}</span>
          <span className="bms-label">Centers</span>
        </div>
        <div className="bms-card">
          <span className="bms-val">{m.complications.rate !== null ? m.complications.rate + '%' : '—'}</span>
          <span className="bms-label">Complication Rate</span>
        </div>
        <div className="bms-card">
          <span className="bms-val">{m.outcomes.improvement_3mo.mean !== null ? m.outcomes.improvement_3mo.mean + '%' : '—'}</span>
          <span className="bms-label">Improvement (3mo)</span>
        </div>
        <div className="bms-card">
          <span className="bms-val">{m.outcomes.satisfaction_3mo.rate !== null ? m.outcomes.satisfaction_3mo.rate + '%' : '—'}</span>
          <span className="bms-label">Satisfaction (3mo)</span>
        </div>
      </div>

      {/* Radar Chart */}
      <div className="bm-section">
        <h2>Performance Overview</h2>
        <div className="bm-radar-wrap">
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#e2e8f0" />
              <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12, fill: '#475569' }} />
              <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
              <Radar name={data.primary_label} dataKey="mine" stroke="#2563eb" fill="#2563eb" fillOpacity={0.15} strokeWidth={2} />
              <Radar name="Network" dataKey="network" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.08} strokeWidth={2} strokeDasharray="5 5" />
              <Legend />
            </RadarChart>
          </ResponsiveContainer>
          <p className="chart-note">Higher = better performance. Network shown as dashed baseline.</p>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="bm-section">
        <h2>Detailed Comparison</h2>
        <div className="bm-table">
          <div className="bmt-header">
            <span className="bmt-metric">Metric</span>
            <span className="bmt-val">{data.primary_label}</span>
            <span className="bmt-val">Network ({n.n})</span>
            <span className="bmt-delta">Δ vs Network</span>
          </div>
          {comparisons.map((row, i) => (
            <div key={i} className="bmt-row">
              <span className="bmt-metric">{row.label}</span>
              <span className="bmt-val">{row.mine !== null ? row.mine + row.unit : '—'}</span>
              <span className="bmt-val">{row.network !== null ? row.network + row.unit : '—'}</span>
              <span className="bmt-delta" style={{color: getDeltaColor(row), fontWeight: 700}}>
                {getDeltaLabel(row)}
              </span>
            </div>
          ))}
        </div>
        <p className="chart-note">
          <span style={{color:'#059669'}}>● Green</span> = better than network · 
          <span style={{color:'#dc2626'}}>● Red</span> = below network · 
          <span style={{color:'#64748b'}}>● Gray</span> = context (no better/worse)
        </p>
      </div>

      {/* Distribution Charts */}
      <div className="bm-section">
        <h2>Patient Distributions</h2>
        <div className="bm-dist-grid">
          {distCharts.map(chart => {
            const myDist = m.distributions[chart.key] || [];
            const netDist = n.distributions[chart.key] || [];
            const merged = myDist.map((bin, i) => ({
              label: bin.label,
              mine: bin.count,
              network: netDist[i] ? netDist[i].count : 0,
            }));
            return (
              <div key={chart.key} className="bm-dist-card">
                <h3>{chart.label}</h3>
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart data={merged} barGap={0}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" fontSize={9} interval={0} angle={-30} textAnchor="end" height={40} />
                    <YAxis fontSize={10} />
                    <Tooltip />
                    <Bar dataKey="mine" fill={chart.color} fillOpacity={0.8} name="Mine" radius={[3,3,0,0]} />
                    <Bar dataKey="network" fill="#cbd5e1" name="Network" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sample Size Warning */}
      {data.center_count <= 1 && (
        <div className="bm-notice">
          📌 Currently showing data from {data.center_count} center ({data.primary_label}). As more centers contribute data, the network comparison will reveal how your outcomes compare. Your data establishes the baseline for future benchmarking.
        </div>
      )}

      {/* Data provenance */}
      <div className="bm-provenance">
        <h3>Data Sources</h3>
        <div className="bm-prov-grid">
          <div className="bm-prov-item">
            <span className="bm-prov-icon">🏥</span>
            <span className="bm-prov-label">{data.center_count} center{data.center_count !== 1 ? 's' : ''}</span>
          </div>
          <div className="bm-prov-item">
            <span className="bm-prov-icon">👨‍⚕️</span>
            <span className="bm-prov-label">{data.has_personal ? 'Personal cases' : 'Institutional upload'}</span>
          </div>
          <div className="bm-prov-item">
            <span className="bm-prov-icon">📊</span>
            <span className="bm-prov-label">{n.n} total patients</span>
          </div>
        </div>
        <p className="chart-note">When you upload data personally, your individual outcomes will be compared separately from your center.</p>
      </div>
    </div>
  );
}

export default Benchmark;
